import React from 'react';

import { Box, Grid, Typography } from '@mui/material';
import _ from 'es-toolkit/compat';

import { Field, Form } from '@hrlogics/react-components/form';

import { CORPORATE_STRUCTURE_OPTIONS, INDUSTRIES_OPTIONS } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import {
  EmployerInfoFragment,
  EmployersTableListDocument,
  useEmployerUpdateMutation,
} from 'src/api';
import { Button } from 'src/components/common';
import { DefaultDisclaimerInputSection } from 'src/components/disclaimer';
import {
  AddressFields,
  AutocompleteField,
  MaskedTextField,
  NumberField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TAX_YEAR_END_DATE_MASK } from 'src/constants/masks';
import { useNotification } from 'src/hooks/app';
import { useDemoPrivacyInputSlotProps } from 'src/hooks/demoPrivacyMode';
import { validateWithYupSchema } from 'src/utils/yup';

import {
  EmployerCompanyInformationFormFields,
  EmployerCompanyInformationFormValues,
} from './types';
import {
  EmployerCompanyInformationFormValidationSchema,
  getEmployerCompanyInformationInitialValues,
} from './utils';

type EmployerCompanyInformationFormProps = {
  employer?: Nullable<EmployerInfoFragment>;
};

export const EmployerCompanyInformationForm: React.FC<EmployerCompanyInformationFormProps> = ({
  employer,
}) => {
  const { createApolloContext } = useNotification();
  const demoPrivacyInputSlotProps = useDemoPrivacyInputSlotProps();

  const [employerUpdateMutation] = useEmployerUpdateMutation({
    refetchQueries: [EmployersTableListDocument],
    context: createApolloContext({ successMessage: 'Employer successfully updated' }),
  });

  const initialValues = React.useMemo(
    () => getEmployerCompanyInformationInitialValues(employer),
    [employer],
  );

  const onSubmit = React.useCallback(
    async (formData: EmployerCompanyInformationFormValues) => {
      try {
        const headquarters = formData[EmployerCompanyInformationFormFields.headquarters];
        await employerUpdateMutation({
          variables: {
            where: { id: employer?.id },
            data: {
              displayName: formData.displayName,
              industry: _.isString(formData.industry)
                ? formData.industry
                : (formData.industry?.value ?? null),
              corporateStructure: formData.corporateStructure ?? null,
              numberOfEmployees: formData.numberOfEmployees ?? null,
              contactEmail: formData.contactEmail?.toLowerCase(),
              contactPhoneNumber: formData.contactPhoneNumber ?? null,
              taxYearEndDate: formData.taxYearEndDate ?? null,
              disclaimer: formData.disclaimer || '',
              headquarters: {
                [!initialValues.headquarters?.id ? 'create' : 'update']: {
                  apartment: headquarters?.apartment ?? null,
                  city: headquarters?.city ?? null,
                  county: headquarters?.county ?? null,
                  state: headquarters?.state ?? null,
                  street: headquarters?.street ?? null,
                  zipCode: headquarters?.zipCode ?? null,
                },
              },
            },
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [employer?.id, employerUpdateMutation, initialValues.headquarters?.id],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ valid: true, values: true, submitting: true }}
      validate={validateWithYupSchema(EmployerCompanyInformationFormValidationSchema)}
    >
      {({ submitting, valid, form }) => {
        const { dirty } = form.getState();

        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Company Display Name"
                name={EmployerCompanyInformationFormFields.displayName}
                fullWidth
                required
                disabled={submitting}
                component={TextField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Industry"
                name={EmployerCompanyInformationFormFields.industry}
                fullWidth
                disabled={submitting}
                options={INDUSTRIES_OPTIONS}
                component={AutocompleteField}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Corporate Structure"
                name={EmployerCompanyInformationFormFields.corporateStructure}
                fullWidth
                disabled={submitting}
                options={CORPORATE_STRUCTURE_OPTIONS}
                component={SelectField}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Field
                fullWidth
                label="Tax Year End Date"
                name={EmployerCompanyInformationFormFields.taxYearEndDate}
                component={MaskedTextField}
                inputProps={{
                  placeholder: 'MM/DD',
                }}
                mask={TAX_YEAR_END_DATE_MASK}
                disabled={submitting}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Field
                label="Number of Employees"
                name={EmployerCompanyInformationFormFields.numberOfEmployees}
                fullWidth
                disabled={submitting}
                component={NumberField}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Primary Contact Phone"
                name={EmployerCompanyInformationFormFields.contactPhoneNumber}
                fullWidth
                disabled={submitting}
                component={PhoneField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Primary Contact Email"
                name={EmployerCompanyInformationFormFields.contactEmail}
                fullWidth
                required
                disabled={submitting}
                component={TextField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1">Headquarters address</Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AddressFields
                prefix={EmployerCompanyInformationFormFields.headquarters}
                requiredStreet
                requiredCity
                requiredState
                requiredZipCode
                disabled={submitting}
                addressLabel="Street"
                inputSlotProps={demoPrivacyInputSlotProps}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <DefaultDisclaimerInputSection
                name={EmployerCompanyInformationFormFields.disclaimer}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box display="flex" justifyContent="center" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => form.reset()}
                  disabled={submitting}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting || !dirty}
                  loading={submitting}
                >
                  Save changes
                </Button>
              </Box>
            </Grid>
          </Grid>
        );
      }}
    </Form>
  );
};
